import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Tab,
    Tabs,
    Alert,
    Box,
    Button,
    ButtonGroup,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import { apiFetch } from '../../lib/api';
import { addDays, isWeekend, type IsoDate } from '../../lib/dates';
import { SectionLabel, PanelCard } from '../primitives';
import { dayLabel, dmy, hrs, type PayNote, type PayrollPeriod } from './types';
import { formatInstantAu } from '../../lib/time';
import { XeroBatchSend } from './XeroBatchSend';

/**
 * Payroll — the legacy "Timesheet Total" screen, for one person and one pay
 * fortnight: a row per payroll category, a column per day, the pay notes, and
 * Send to Xero.
 *
 * Edit turns every cell into an input. A changed cell is saved as an override;
 * setting it back to the calculated figure removes the override. Overridden
 * cells are marked, with the calculated figure in the tooltip, so nothing an
 * admin retyped is invisible afterwards — the legacy screen overwrote the value
 * and kept nothing.
 */

interface Props {
    readonly open: boolean;
    readonly staffIds: readonly number[];
    /** Names for the tab labels. The parent already has the staff list. */
    readonly staffNames: ReadonlyMap<number, string>;
    readonly from: IsoDate;
    readonly onClose: () => void;
    readonly onPeriodChange: (from: IsoDate) => void;
}

function cellKey(kind: string, date: string): string {
    return `${kind}:${date}`;
}

export function PayrollDialog({
    open,
    staffIds,
    staffNames,
    from,
    onClose,
    onPeriodChange,
}: Props): React.JSX.Element {
    /**
     * Which tab is showing. 0..staffIds.length-1 is a person; the last index is
     * the batch Send to Xero tab.
     *
     * The rest of this component already keyed off a single staff id and
     * reloaded when it changed, so a tab switch is just a different id — the
     * grid, the overrides and the pay notes all follow without further work.
     */
    const [tab, setTab] = useState(0);
    const sendTabIndex = staffIds.length;
    const onSendTab = tab === sendTabIndex;
    const staffId = onSendTab ? null : (staffIds[tab] ?? null);

    // A different selection, or a different fortnight, starts at the first
    // person rather than leaving a stale tab index pointing at nobody.
    useEffect(() => {
        setTab(0);
    }, [staffIds, from, open]);

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const [data, setData] = useState<PayrollPeriod | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [warnings, setWarnings] = useState<readonly string[]>([]);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState<Record<string, string>>({});
    const [busy, setBusy] = useState(false);
    const [nonce, setNonce] = useState(0);

    const reload = useCallback(() => {
        setNonce((n) => n + 1);
    }, []);

    useEffect(() => {
        if (!open || staffId === null) {
            return;
        }
        let cancelled = false;
        setLoading(true);
        apiFetch<PayrollPeriod>(`/timesheets/payroll?staffId=${String(staffId)}&from=${from}`)
            .then((r) => {
                if (!cancelled) {
                    setData(r);
                    setError(null);
                }
            })
            .catch((err: unknown) => {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : 'Could not load payroll');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [open, staffId, from, nonce]);

    useEffect(() => {
        if (!open) {
            setEditing(false);
            setDraft({});
            setNotice(null);
            setWarnings([]);
            setError(null);
            setData(null);
        }
    }, [open]);

    const changed = useMemo(() => {
        if (data === null) {
            return [];
        }
        const out: { date: string; kind: string; hours: number | null }[] = [];
        for (const row of data.rows) {
            for (const cell of row.cells) {
                const raw = draft[cellKey(row.kind, cell.date)];
                if (raw === undefined) {
                    continue;
                }
                const typed = raw.trim() === '' ? 0 : Number(raw);
                if (!Number.isFinite(typed)) {
                    continue;
                }
                const next = Math.round(typed * 100) / 100;
                if (next === cell.value) {
                    continue;
                }
                // Back to what the entries give: drop the override instead of storing a copy.
                out.push({ date: cell.date, kind: row.kind, hours: next === cell.computed ? null : next });
            }
        }
        return out;
    }, [data, draft]);

    const invalid = Object.values(draft).some((v) => v.trim() !== '' && (!Number.isFinite(Number(v)) || Number(v) < 0 || Number(v) > 24));

    function save(): void {
        if (data === null) {
            return;
        }
        setBusy(true);
        setError(null);
        apiFetch<unknown>('/timesheets/payroll/overrides', {
            method: 'PUT',
            body: JSON.stringify({ staffId: data.staff.staffId, cells: changed }),
        })
            .then(() => {
                setEditing(false);
                setDraft({});
                setNotice('Payroll updated');
                reload();
            })
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'Could not save');
            })
            .finally(() => {
                setBusy(false);
            });
    }

    function resetCell(kind: string, date: string, computed: number): void {
        setDraft((d) => ({ ...d, [cellKey(kind, date)]: String(computed) }));
    }

    /**
     * Sign off this fortnight, or take the sign-off back.
     *
     * This replaced a per-tab "Send to Xero" button. Reviewing and sending were
     * the same action, so nothing recorded that a person had CHECKED the hours
     * — only that they had been uploaded. Approving records who and when;
     * sending is the batch step on the last tab, and it refuses anyone without
     * a live approval.
     */
    function setApproval(approve: boolean): void {
        if (data === null) {
            return;
        }
        setBusy(true);
        setError(null);
        apiFetch(approve ? '/timesheets/payroll/approve' : '/timesheets/payroll/withdraw-approval', {
            method: 'POST',
            body: JSON.stringify({ staffId: data.staff.staffId, from: data.from }),
        })
            .then(() => {
                setNotice(approve ? 'Approved' : 'Approval withdrawn');
                reload();
            })
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'Could not change the approval');
            })
            .finally(() => {
                setBusy(false);
            });
    }

    /**
     * Why Approve is unavailable, or null.
     *
     * Deliberately fewer tests than sending has. Whether someone is linked to a
     * Xero employee is a question for the moment of sending, not for whether
     * their hours are correct — the office should be able to sign off a
     * fortnight while somebody chases the Xero id.
     */
    const approveBlocked = useMemo(() => {
        if (data === null) {
            return 'Loading';
        }
        if (editing) {
            return 'Save or cancel your changes first';
        }
        if (data.rows.length === 0) {
            return 'No payroll categories set up on their staff record';
        }
        return null;
    }, [data, editing]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth fullScreen={fullScreen} slotProps={{ paper: { elevation: 1 } }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                Payroll
                <Typography component="span" sx={{ color: 'text.secondary', fontSize: '0.95rem' }}>
                    {onSendTab
                        ? `${String(staffIds.length)} staff member${staffIds.length === 1 ? '' : 's'}`
                        : (data?.staff.name ?? '')}
                </Typography>
                <ButtonGroup size="small" variant="outlined" disabled={editing || busy}>
                    <Button
                        aria-label="Previous pay period"
                        onClick={() => {
                            onPeriodChange(addDays(from, -14));
                        }}
                    >
                        <ChevronLeftIcon fontSize="small" />
                    </Button>
                    <Button sx={{ whiteSpace: 'nowrap', pointerEvents: 'none' }}>
                        {dmy(from)} – {dmy(addDays(from, 13))}
                    </Button>
                    <Button
                        aria-label="Next pay period"
                        onClick={() => {
                            onPeriodChange(addDays(from, 14));
                        }}
                    >
                        <ChevronRightIcon fontSize="small" />
                    </Button>
                </ButtonGroup>
                {data !== null && data.upload !== null ? (
                    <Chip
                        size="small"
                        color="warning"
                        variant="outlined"
                        label={`Uploaded to Xero ${dmy(data.upload.uploadedAt.slice(0, 10))}${data.upload.uploadedBy === null ? '' : ` by ${data.upload.uploadedBy}`}`}
                    />
                ) : null}
                {loading ? <CircularProgress size={18} /> : null}
                <Box sx={{ flex: 1 }} />
                <IconButton onClick={onClose} size="small" disabled={busy} aria-label="Close">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            {/* One tab per person, then the batch send. Disabled mid-edit for
                the same reason the period arrows are: switching away would
                silently drop the cells being typed. */}
            <Tabs
                value={tab}
                onChange={(_e: React.SyntheticEvent, next: number) => {
                    setTab(next);
                }}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ px: 2, borderBottom: 1, borderColor: 'divider', minHeight: 40 }}
            >
                {staffIds.map((id) => (
                    <Tab
                        key={id}
                        disabled={editing || busy}
                        sx={{ minHeight: 40, textTransform: 'none' }}
                        label={staffNames.get(id) ?? `Staff ${String(id)}`}
                    />
                ))}
                <Tab
                    disabled={editing || busy}
                    icon={<CloudUploadIcon fontSize="small" />}
                    iconPosition="start"
                    sx={{ minHeight: 40, textTransform: 'none', fontWeight: 700 }}
                    label="Send to Xero"
                />
            </Tabs>

            <DialogContent dividers>
                {onSendTab ? (
                    <XeroBatchSend
                        staffIds={staffIds}
                        from={from}
                        onSent={() => {
                            // The per-person tabs show an "Uploaded to Xero"
                            // chip that is now out of date.
                            reload();
                        }}
                    />
                ) : (
                <>
                {error !== null ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                ) : null}
                {notice !== null ? (
                    <Alert
                        severity="success"
                        sx={{ mb: 2 }}
                        onClose={() => {
                            setNotice(null);
                        }}
                    >
                        {notice}
                    </Alert>
                ) : null}
                {warnings.length > 0 ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {warnings.map((w) => (
                            <div key={w}>{w}</div>
                        ))}
                    </Alert>
                ) : null}

                {data !== null && data.rows.length === 0 ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {data.staff.name} has no payroll categories. Set them on their record on the Staff page.
                    </Alert>
                ) : null}

                {data !== null && data.rows.length > 0 ? (
                    <PanelCard>
                        <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }} flexWrap="wrap">
                            <Box sx={{ flex: 1 }}>
                                <SectionLabel>Hours by payroll category</SectionLabel>
                            </Box>
                            {editing ? (
                                <>
                                    <Button
                                        color="inherit"
                                        disabled={busy}
                                        onClick={() => {
                                            setEditing(false);
                                            setDraft({});
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="contained"
                                        startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                                        disabled={busy || invalid || changed.length === 0}
                                        onClick={save}
                                    >
                                        {changed.length === 0 ? 'Update' : `Update ${String(changed.length)}`}
                                    </Button>
                                </>
                            ) : (
                                <Button
                                    variant="outlined"
                                    startIcon={<EditIcon />}
                                    disabled={busy || loading}
                                    onClick={() => {
                                        setEditing(true);
                                    }}
                                >
                                    Edit
                                </Button>
                            )}
                        </Stack>

                        <TableContainer sx={{ overflowX: 'auto' }}>
                            <Table size="small" sx={{ minWidth: 1100 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ minWidth: 170, fontWeight: 700 }}>Payroll Category</TableCell>
                                        {data.dates.map((d) => (
                                            <TableCell
                                                key={d}
                                                align="right"
                                                sx={{
                                                    minWidth: 66,
                                                    whiteSpace: 'nowrap',
                                                    fontWeight: 700,
                                                    color: isWeekend(d) ? 'text.disabled' : undefined,
                                                }}
                                            >
                                                {dayLabel(d)}
                                            </TableCell>
                                        ))}
                                        <TableCell align="right" sx={{ fontWeight: 800 }}>
                                            Total
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.rows.map((row, idx) => (
                                        <TableRow
                                            key={row.kind}
                                            sx={{ bgcolor: idx % 2 !== 0 ? 'action.hover' : 'transparent' }}
                                        >
                                            <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                                                {row.name}
                                                {!row.countsTowardTotal ? (
                                                    <Tooltip title="Lunch already comes off base hours; shown for reference and not added to the total">
                                                        <Typography
                                                            component="span"
                                                            sx={{ ml: 0.75, fontSize: '0.7rem', color: 'text.secondary' }}
                                                        >
                                                            (info)
                                                        </Typography>
                                                    </Tooltip>
                                                ) : null}
                                            </TableCell>
                                            {row.cells.map((cell) => {
                                                const key = cellKey(row.kind, cell.date);
                                                if (editing) {
                                                    const draftValue = draft[key];
                                                    const shown = draftValue ?? (cell.value === 0 ? '' : String(cell.value));
                                                    const differs =
                                                        draftValue !== undefined
                                                            ? Number(draftValue || 0) !== cell.computed
                                                            : cell.override !== null;
                                                    return (
                                                        <TableCell key={cell.date} align="right" sx={{ px: 0.5 }}>
                                                            <Stack direction="row" alignItems="center" justifyContent="flex-end">
                                                                <TextField
                                                                    size="small"
                                                                    value={shown}
                                                                    onChange={(e) => {
                                                                        setDraft((d) => ({ ...d, [key]: e.target.value }));
                                                                    }}
                                                                    slotProps={{
                                                                        htmlInput: {
                                                                            inputMode: 'decimal',
                                                                            'aria-label': `${row.name} ${dayLabel(cell.date)}`,
                                                                            style: { textAlign: 'right', padding: '4px 6px', width: 44 },
                                                                        },
                                                                    }}
                                                                    sx={differs ? { '& fieldset': { borderColor: 'info.main' } } : {}}
                                                                />
                                                                {differs ? (
                                                                    <Tooltip title={`Back to calculated ${cell.computed.toFixed(2)}`}>
                                                                        <IconButton
                                                                            size="small"
                                                                            sx={{ p: 0.25 }}
                                                                            onClick={() => {
                                                                                resetCell(row.kind, cell.date, cell.computed);
                                                                            }}
                                                                        >
                                                                            <UndoIcon sx={{ fontSize: 14 }} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                ) : null}
                                                            </Stack>
                                                        </TableCell>
                                                    );
                                                }
                                                return (
                                                    <TableCell
                                                        key={cell.date}
                                                        align="right"
                                                        sx={{
                                                            color:
                                                                cell.override !== null
                                                                    ? 'info.main'
                                                                    : cell.value === 0
                                                                      ? 'text.disabled'
                                                                      : undefined,
                                                            fontWeight: cell.override !== null ? 700 : 400,
                                                        }}
                                                    >
                                                        {cell.override !== null ? (
                                                            <Tooltip title={`Changed by hand. Calculated: ${cell.computed.toFixed(2)}`}>
                                                                <span>{hrs(cell.value)}*</span>
                                                            </Tooltip>
                                                        ) : (
                                                            hrs(cell.value)
                                                        )}
                                                    </TableCell>
                                                );
                                            })}
                                            <TableCell align="right" sx={{ fontWeight: 800 }}>
                                                {row.total.toFixed(2)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 800 }}>Total</TableCell>
                                        {data.dayTotals.map((t, i) => (
                                            <TableCell key={data.dates[i]} align="right" sx={{ fontWeight: 800 }}>
                                                {hrs(t)}
                                            </TableCell>
                                        ))}
                                        <TableCell align="right" sx={{ fontWeight: 800, color: 'primary.main' }}>
                                            {data.grandTotal.toFixed(2)}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Typography sx={{ mt: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
                            * changed by hand. Base Hours are hours worked less overtime. Trade School is sent to Xero as
                            Base Hours, and annual, sick, compassionate and unpaid leave as leave applications.
                        </Typography>
                    </PanelCard>
                ) : null}

                {data !== null ? (
                    <Box sx={{ mt: 2.5 }}>
                        <PayNotes
                            staffId={data.staff.staffId}
                            from={data.from}
                            to={data.to}
                            notes={data.notes}
                            onChanged={reload}
                        />
                        <ApprovalHistory period={data} />
                    </Box>
                ) : null}
                </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                {onSendTab ? null : data !== null && data.approval !== null ? (
                    <Stack direction="row" alignItems="center" gap={1.5} sx={{ mr: 'auto' }} flexWrap="wrap">
                        <Chip
                            size="small"
                            color="success"
                            icon={<CheckCircleIcon />}
                            label={`Approved ${dmy(data.approval.approvedAt.slice(0, 10))}${
                                data.approval.approvedBy === null ? '' : ` by ${data.approval.approvedBy}`
                            }`}
                        />
                        <Button size="small" disabled={busy || editing} onClick={() => { setApproval(false); }}>
                            Withdraw approval
                        </Button>
                    </Stack>
                ) : (
                    <Tooltip title={approveBlocked ?? ''}>
                        <span style={{ marginRight: 'auto' }}>
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<CheckCircleIcon />}
                                disabled={approveBlocked !== null || busy}
                                onClick={() => { setApproval(true); }}
                            >
                                Approve
                            </Button>
                        </span>
                    </Tooltip>
                )}
                <Button onClick={onClose} disabled={busy}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ─── History ────────────────────────────────────────────────────────────────

/**
 * Every approval, withdrawal and Xero submission for this fortnight.
 *
 * Both lists are append-only in the database — withdrawing an approval marks
 * the row rather than deleting it, and re-sending appends a second upload —
 * so this is the whole story rather than the current state. "Approved,
 * withdrawn when Dean's Tuesday was corrected, approved again, sent" is four
 * lines here and is exactly what anybody asks for when a pay run is queried.
 *
 * The legacy app kept the sends (`xero_upload`, 1,297 rows) but never showed
 * them: the only visible trace was the Send button turning into a label once
 * you navigated back to that staff member and fortnight.
 */
function ApprovalHistory({ period }: { period: PayrollPeriod }): React.JSX.Element | null {
    const events: { at: string; text: string; kind: 'approved' | 'withdrawn' | 'sent' }[] = [];

    for (const a of period.approvalHistory) {
        events.push({
            at: a.approvedAt,
            kind: 'approved',
            text:
                `Approved${a.approvedBy === null ? '' : ` by ${a.approvedBy}`}` +
                (a.approvedTotalHours === null ? '' : ` at ${a.approvedTotalHours.toFixed(2)} hours`),
        });
        if (a.withdrawnAt !== null) {
            events.push({
                at: a.withdrawnAt,
                kind: 'withdrawn',
                text: `Approval withdrawn${a.withdrawnBy === null ? '' : ` by ${a.withdrawnBy}`}`,
            });
        }
    }
    for (const u of period.uploads) {
        events.push({
            at: u.uploadedAt,
            kind: 'sent',
            text: `Sent to Xero${u.uploadedBy === null ? '' : ` by ${u.uploadedBy}`}`,
        });
    }

    if (events.length === 0) {
        return null;
    }
    events.sort((a, b) => b.at.localeCompare(a.at));

    return (
        <Box sx={{ mt: 3 }}>
            <Typography
                sx={{
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                    mb: 1,
                }}
            >
                Approval &amp; Xero history
            </Typography>
            <Stack spacing={0.75}>
                {events.map((e) => (
                    <Stack key={`${e.kind}-${e.at}`} direction="row" alignItems="center" gap={1.25}>
                        <Chip
                            size="small"
                            variant="outlined"
                            color={e.kind === 'sent' ? 'info' : e.kind === 'approved' ? 'success' : 'default'}
                            label={e.kind === 'sent' ? 'Xero' : e.kind === 'approved' ? 'Approved' : 'Withdrawn'}
                            sx={{ minWidth: 92 }}
                        />
                        <Typography variant="body2">{e.text}</Typography>
                        <Box sx={{ flex: 1 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                            {formatInstantAu(e.at)} {new Date(e.at).toLocaleTimeString('en-AU', {
                                timeZone: 'Australia/Melbourne',
                                hour: '2-digit',
                                minute: '2-digit',
                            })}
                        </Typography>
                    </Stack>
                ))}
            </Stack>
        </Box>
    );
}

// ─── Pay notes ──────────────────────────────────────────────────────────────

interface PayNotesProps {
    readonly staffId: number;
    readonly from: IsoDate;
    readonly to: IsoDate;
    readonly notes: readonly PayNote[];
    readonly onChanged: () => void;
}

/** Legacy pay_notes on the Total screen: add, edit and delete, now with a confirm on delete. */
function PayNotes({ staffId, from, to, notes, onChanged }: PayNotesProps): React.JSX.Element {
    const dates = useMemo(() => Array.from({ length: 14 }, (_, i) => addDays(from, i)), [from]);
    const [adding, setAdding] = useState(false);
    const [newDate, setNewDate] = useState(from);
    const [newNote, setNewNote] = useState('');
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editText, setEditText] = useState('');
    const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setNewDate(from);
        setAdding(false);
        setEditingId(null);
    }, [from, to, staffId]);

    function run(request: Promise<unknown>, after: () => void): void {
        setBusy(true);
        setError(null);
        request
            .then(() => {
                after();
                onChanged();
            })
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'That did not work');
            })
            .finally(() => {
                setBusy(false);
            });
    }

    return (
        <PanelCard>
            <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
                <Box sx={{ flex: 1 }}>
                    <SectionLabel>Pay notes</SectionLabel>
                </Box>
                {!adding ? (
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                            setAdding(true);
                        }}
                    >
                        Add Note
                    </Button>
                ) : null}
            </Stack>

            {error !== null ? (
                <Alert severity="error" sx={{ mb: 1.5 }}>
                    {error}
                </Alert>
            ) : null}

            {adding ? (
                <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} sx={{ mb: 2 }} alignItems={{ sm: 'flex-start' }}>
                    <TextField
                        select
                        size="small"
                        label="Day"
                        value={newDate}
                        onChange={(e) => {
                            setNewDate(e.target.value);
                        }}
                        sx={{ minWidth: 150 }}
                    >
                        {dates.map((d) => (
                            <MenuItem key={d} value={d}>
                                {dayLabel(d)}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        size="small"
                        label="Note"
                        fullWidth
                        multiline
                        autoFocus
                        value={newNote}
                        onChange={(e) => {
                            setNewNote(e.target.value);
                        }}
                    />
                    <Stack direction="row" gap={1}>
                        <Button
                            disabled={busy}
                            onClick={() => {
                                setAdding(false);
                                setNewNote('');
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            disabled={busy || newNote.trim() === ''}
                            onClick={() => {
                                run(
                                    apiFetch('/timesheets/pay-notes', {
                                        method: 'POST',
                                        body: JSON.stringify({ staffId, noteDate: newDate, note: newNote }),
                                    }),
                                    () => {
                                        setAdding(false);
                                        setNewNote('');
                                    },
                                );
                            }}
                        >
                            Save
                        </Button>
                    </Stack>
                </Stack>
            ) : null}

            {notes.length === 0 ? (
                <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>No pay notes this fortnight.</Typography>
            ) : (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: 110 }}>Date</TableCell>
                            <TableCell>Comments</TableCell>
                            <TableCell sx={{ width: 160 }}>Updated By</TableCell>
                            <TableCell align="right" sx={{ width: 110 }}>
                                Action
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {notes.map((n) => (
                            <TableRow key={n.id}>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{dayLabel(n.noteDate)}</TableCell>
                                <TableCell>
                                    {editingId === n.id ? (
                                        <TextField
                                            size="small"
                                            fullWidth
                                            multiline
                                            autoFocus
                                            value={editText}
                                            onChange={(e) => {
                                                setEditText(e.target.value);
                                            }}
                                        />
                                    ) : (
                                        <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>{n.note}</Typography>
                                    )}
                                </TableCell>
                                <TableCell sx={{ color: 'text.secondary' }}>{n.updatedBy ?? '—'}</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                    {editingId === n.id ? (
                                        <>
                                            <Button size="small" disabled={busy} onClick={() => { setEditingId(null); }}>
                                                Cancel
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                disabled={busy || editText.trim() === ''}
                                                onClick={() => {
                                                    run(
                                                        apiFetch(`/timesheets/pay-notes/${String(n.id)}`, {
                                                            method: 'PATCH',
                                                            body: JSON.stringify({ note: editText }),
                                                        }),
                                                        () => {
                                                            setEditingId(null);
                                                        },
                                                    );
                                                }}
                                            >
                                                Save
                                            </Button>
                                        </>
                                    ) : confirmDelete === n.id ? (
                                        <>
                                            <Button size="small" disabled={busy} onClick={() => { setConfirmDelete(null); }}>
                                                Keep
                                            </Button>
                                            <Button
                                                size="small"
                                                color="error"
                                                variant="contained"
                                                disabled={busy}
                                                onClick={() => {
                                                    run(apiFetch(`/timesheets/pay-notes/${String(n.id)}`, { method: 'DELETE' }), () => {
                                                        setConfirmDelete(null);
                                                    });
                                                }}
                                            >
                                                Delete
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            <Tooltip title="Edit">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => {
                                                        setEditingId(n.id);
                                                        setEditText(n.note);
                                                    }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => {
                                                        setConfirmDelete(n.id);
                                                    }}
                                                >
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </PanelCard>
    );
}
